import { useState, useEffect } from "react";
import PageBreadcrumb from "../../components/common/PageBreadCrumb";
import PageMeta from "../../components/common/PageMeta";
import {
  fetchUserProfile,
  updateUserProfile,
  changePassword,
  UserProfile,
} from "../../lib/api/user";
import UsageSection from "../../components/profile/UsageSection";
import Workspaces from "./Workspaces";
import Members from "./Members";
import BusinessProfileForm from "../../components/regulagent/BusinessProfileForm";

const baseTabs = ["Profile", "Usage & Billing", "Workspaces", "Members"] as const;
type Tab = (typeof baseTabs)[number] | "Business Profile";

export default function Settings() {
  const [activeTab, setActiveTab] = useState<Tab>("Profile");

  // Profile state
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    title: "",
    phone: "",
    organization: "",
  });

  const [passwordData, setPasswordData] = useState({
    old_password: "",
    new_password: "",
    confirm_password: "",
  });

  useEffect(() => {
    const loadProfile = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await fetchUserProfile();
        setProfile(data);
        setFormData({
          first_name: data.first_name || "",
          last_name: data.last_name || "",
          title: data.title || "",
          phone: data.phone || "",
          organization: data.organization || "",
        });
      } catch (err) {
        console.error("[Settings] Error loading profile:", err);
        setError(err instanceof Error ? err.message : "Failed to load profile");
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveProfile = async () => {
    try {
      setIsSaving(true);
      setError(null);
      const updated = await updateUserProfile(formData);
      setProfile(updated);
      setIsEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update profile");
    } finally {
      setIsSaving(false);
    }
  };

  const handleChangePassword = async () => {
    setPasswordError(null);
    setPasswordSuccess(false);

    if (!passwordData.old_password || !passwordData.new_password) {
      setPasswordError("Please fill in all password fields");
      return;
    }
    if (passwordData.new_password !== passwordData.confirm_password) {
      setPasswordError("New passwords do not match");
      return;
    }
    if (passwordData.new_password.length < 8) {
      setPasswordError("New password must be at least 8 characters");
      return;
    }

    try {
      await changePassword(passwordData.old_password, passwordData.new_password);
      setPasswordSuccess(true);
      setPasswordData({ old_password: "", new_password: "", confirm_password: "" });
      setTimeout(() => {
        setShowPasswordForm(false);
        setPasswordSuccess(false);
      }, 2000);
    } catch (err) {
      setPasswordError(err instanceof Error ? err.message : "Failed to change password");
    }
  };

  return (
    <>
      <PageMeta title="Settings | RegulAgent" description="Manage your account settings and workspaces." />
      <PageBreadcrumb pageTitle="Settings" />

      <div className="flex gap-1 border-b border-gray-200 dark:border-white/[0.05] mb-6">
        {(profile?.is_tenant_admin
          ? ([...baseTabs, "Business Profile"] as const)
          : baseTabs
        ).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab
                ? "border-brand-500 text-brand-600 dark:text-brand-400"
                : "border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400"
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {activeTab === "Profile" && (
        <div className="space-y-6">
          {loading && (
            <div className="flex items-center py-8">
              <div className="inline-block h-6 w-6 animate-spin rounded-full border-4 border-brand-500 border-r-transparent" />
              <p className="ml-3 text-sm text-gray-600 dark:text-gray-400">Loading profile...</p>
            </div>
          )}

          {error && !profile && (
            <div className="rounded-lg bg-error-50 p-4 dark:bg-error-500/15">
              <p className="text-sm font-medium text-error-700 dark:text-error-400">{error}</p>
            </div>
          )}

          {!loading && profile && (
            <>
              {/* General Information */}
              <div className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-white/[0.03] lg:p-6">
                <div className="mb-5 flex items-center justify-between lg:mb-7">
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
                    General Information
                  </h3>
                  {!isEditing && (
                    <button
                      onClick={() => setIsEditing(true)}
                      className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 transition-colors"
                    >
                      Edit
                    </button>
                  )}
                </div>

                {error && isEditing && (
                  <div className="mb-4 rounded-lg bg-error-50 p-3 dark:bg-error-500/15">
                    <p className="text-sm text-error-700 dark:text-error-400">{error}</p>
                  </div>
                )}

                <div className="grid gap-6 md:grid-cols-2">
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">Email</label>
                    <input type="email" value={profile.email || ""} disabled
                      className="w-full rounded-lg border border-gray-300 bg-gray-50 px-4 py-2 text-gray-700 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-400" />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">First Name</label>
                    <input type="text" name="first_name" value={formData.first_name} onChange={handleInputChange} disabled={!isEditing}
                      className={`w-full rounded-lg border px-4 py-2 ${isEditing ? "border-gray-300 bg-white text-gray-800 dark:border-gray-600 dark:bg-gray-900 dark:text-white" : "border-gray-300 bg-gray-50 text-gray-700 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-400"}`} />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">Last Name</label>
                    <input type="text" name="last_name" value={formData.last_name} onChange={handleInputChange} disabled={!isEditing}
                      className={`w-full rounded-lg border px-4 py-2 ${isEditing ? "border-gray-300 bg-white text-gray-800 dark:border-gray-600 dark:bg-gray-900 dark:text-white" : "border-gray-300 bg-gray-50 text-gray-700 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-400"}`} />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">Title</label>
                    <input type="text" name="title" value={formData.title} onChange={handleInputChange} disabled={!isEditing} placeholder="e.g., Petroleum Engineer"
                      className={`w-full rounded-lg border px-4 py-2 ${isEditing ? "border-gray-300 bg-white text-gray-800 dark:border-gray-600 dark:bg-gray-900 dark:text-white" : "border-gray-300 bg-gray-50 text-gray-700 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-400"}`} />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">Phone</label>
                    <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} disabled={!isEditing} placeholder="e.g., +1 (555) 123-4567"
                      className={`w-full rounded-lg border px-4 py-2 ${isEditing ? "border-gray-300 bg-white text-gray-800 dark:border-gray-600 dark:bg-gray-900 dark:text-white" : "border-gray-300 bg-gray-50 text-gray-700 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-400"}`} />
                  </div>
                  <div className="md:col-span-2">
                    <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">Organization</label>
                    <input type="text" name="organization" value={formData.organization} onChange={handleInputChange} disabled={!isEditing} placeholder="e.g., Acme Oil & Gas"
                      className={`w-full rounded-lg border px-4 py-2 ${isEditing ? "border-gray-300 bg-white text-gray-800 dark:border-gray-600 dark:bg-gray-900 dark:text-white" : "border-gray-300 bg-gray-50 text-gray-700 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-400"}`} />
                  </div>
                </div>

                {isEditing && (
                  <div className="mt-6 flex gap-3">
                    <button onClick={handleSaveProfile} disabled={isSaving}
                      className="rounded-lg bg-brand-500 px-6 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50 transition-colors">
                      {isSaving ? "Saving..." : "Save Changes"}
                    </button>
                    <button onClick={() => setIsEditing(false)} disabled={isSaving}
                      className="rounded-lg border border-gray-300 px-6 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-900 transition-colors">
                      Cancel
                    </button>
                  </div>
                )}
              </div>

              {/* Tenant Information */}
              {profile.tenant && (
                <div className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-white/[0.03] lg:p-6">
                  <h3 className="mb-5 text-lg font-semibold text-gray-800 dark:text-white/90 lg:mb-7">
                    Tenant Information
                  </h3>
                  <div className="grid gap-6 md:grid-cols-2">
                    <div>
                      <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">Tenant Name</label>
                      <input type="text" value={profile.tenant.name} disabled
                        className="w-full rounded-lg border border-gray-300 bg-gray-50 px-4 py-2 text-gray-700 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-400" />
                    </div>
                    <div>
                      <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">Tenant Slug</label>
                      <input type="text" value={profile.tenant.slug} disabled
                        className="w-full rounded-lg border border-gray-300 bg-gray-50 px-4 py-2 text-gray-700 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-400" />
                    </div>
                  </div>
                </div>
              )}

              {/* Security */}
              <div className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-white/[0.03] lg:p-6">
                <div className="mb-5 flex items-center justify-between lg:mb-7">
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">Security</h3>
                  {!showPasswordForm && (
                    <button onClick={() => setShowPasswordForm(true)}
                      className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 transition-colors">
                      Change Password
                    </button>
                  )}
                </div>

                {!showPasswordForm && (
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Click "Change Password" above to update your password. Please ensure your new password is at least 8 characters long.
                  </p>
                )}

                {showPasswordForm && (
                  <div className="space-y-4">
                    {passwordError && (
                      <div className="rounded-lg bg-error-50 p-3 dark:bg-error-500/15">
                        <p className="text-sm text-error-700 dark:text-error-400">{passwordError}</p>
                      </div>
                    )}
                    {passwordSuccess && (
                      <div className="rounded-lg bg-success-50 p-3 dark:bg-success-500/15">
                        <p className="text-sm text-success-700 dark:text-success-400">Password changed successfully!</p>
                      </div>
                    )}
                    <div>
                      <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">Current Password</label>
                      <input type="password" name="old_password" value={passwordData.old_password} onChange={handlePasswordChange} placeholder="Enter your current password"
                        className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-800 dark:border-gray-600 dark:bg-gray-900 dark:text-white" />
                    </div>
                    <div>
                      <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">New Password</label>
                      <input type="password" name="new_password" value={passwordData.new_password} onChange={handlePasswordChange} placeholder="Minimum 8 characters"
                        className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-800 dark:border-gray-600 dark:bg-gray-900 dark:text-white" />
                    </div>
                    <div>
                      <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">Confirm New Password</label>
                      <input type="password" name="confirm_password" value={passwordData.confirm_password} onChange={handlePasswordChange} placeholder="Confirm your new password"
                        className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-800 dark:border-gray-600 dark:bg-gray-900 dark:text-white" />
                    </div>
                    <div className="flex gap-3 pt-2">
                      <button onClick={handleChangePassword}
                        className="rounded-lg bg-brand-500 px-6 py-2 text-sm font-medium text-white hover:bg-brand-600 transition-colors">
                        Update Password
                      </button>
                      <button onClick={() => { setShowPasswordForm(false); setPasswordError(null); setPasswordData({ old_password: "", new_password: "", confirm_password: "" }); }}
                        className="rounded-lg border border-gray-300 px-6 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-900 transition-colors">
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {activeTab === "Usage & Billing" && (
        <UsageSection isTenantAdmin={profile?.is_tenant_admin} />
      )}

      {activeTab === "Workspaces" && <Workspaces />}

      {activeTab === "Members" && <Members />}

      {activeTab === "Business Profile" && profile?.is_tenant_admin && (
        <BusinessProfileForm />
      )}
    </>
  );
}
